<template>
  <div>
    <!-- Modal pour affichage en grand -->
    <div 
      v-if="selectedImage" 
      class="fixed inset-0 bg-black z-50 flex items-center justify-center p-4"
      @click="closeModal"
    >
      <!-- Container responsive pour l'image -->
      <div class="relative w-full h-full md:w-auto md:h-auto md:max-w-none md:max-h-[90vh] flex items-center justify-center">
        <img 
          :src="selectedImage" 
          class="w-full h-full object-contain md:w-auto md:h-auto md:max-h-[90vh] md:max-w-[90vw]"
          @click.stop
        >
        <button 
          @click="closeModal"
          class="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <!-- Navigation -->
        <button 
          v-if="selectedImageIndex > 0"
          @click.stop="previousImage"
          class="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full p-3 hover:bg-opacity-70 transition-all"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <button 
          v-if="selectedImageIndex < activeModalImages.length - 1"
          @click.stop="nextImage"
          class="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full p-3 hover:bg-opacity-70 transition-all"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Mur de photos -->
    <div v-if="images.length">
      <!-- Bouton retour optionnel -->
      <div
        v-if="showBackButton"
        class="text-center mb-8"
      >
        <button 
          @click="goBack"
          class="inline-flex items-center gap-3 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all duration-300 transform hover:scale-105 backdrop-blur-md border border-white/20"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {{ backButtonText || 'Retour' }}
        </button>
      </div>

      <!-- Mur de photos en lignes -->
      <div class="flex justify-center">
        <div class="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-2 gap-y-4 py-4 px-1 max-w-7xl items-center">
          <div
            v-for="(image, index) in images"
            :key="getImageSrc(image)"
            class="group cursor-pointer transition-all duration-300 hover:z-10 relative"
            @click="handleImageClick(image, index)"
          >
            <div class="relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 aspect-square">
              <img
                :src="getImageSrc(image)"
                class="w-full h-full object-cover group-hover:brightness-110 transition-all duration-300"
                loading="lazy"
              >
              <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                <svg
                  class="w-8 h-8 md:w-12 md:h-12 text-white opacity-0 group-hover:opacity-100 transition-all duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    stroke-width="2" 
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <!-- Titre du thème si disponible et showTitles est activé -->
              <div 
                v-if="showTitles && getImageTitle(image)"
                class="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white p-2 text-center text-sm font-medium"
              >
                {{ getImageTitle(image) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bouton retour aux thèmes -->
    <transition name="fade">
      <button
        v-if="showScrollToTop && showBackButton"
        @click="goBack"
        class="fixed bottom-6 right-6 z-40 w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 flex items-center justify-center"
        aria-label="Retour aux thèmes"
      >
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path 
            stroke-linecap="round" 
            stroke-linejoin="round" 
            stroke-width="2" 
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'PhotoWall',
  props: {
    images: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    },
    showTitles: {
      type: Boolean,
      default: false
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    backButtonText: {
      type: String,
      default: 'Retour'
    }
  },
  data() {
    return {
      selectedImage: null,
      selectedImageIndex: -1,
      modalImages: [], // Images spécifiques pour le modal
      showScrollToTop: false // Pour afficher/masquer le bouton retour aux thèmes
    }
  },
  computed: {
    // Images actives pour le modal (modalImages si spécifiées, sinon images props)
    activeModalImages() {
      return this.modalImages.length > 0 ? this.modalImages : this.images;
    }
  },
  mounted() {
    // Gestionnaire d'événements clavier pour le modal
    document.addEventListener('keydown', this.handleKeydown);
    
    // Gestionnaire de scroll pour le bouton retour aux thèmes
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    // Nettoyer les événements clavier
    document.removeEventListener('keydown', this.handleKeydown);
    
    // Nettoyer l'événement de scroll
    window.removeEventListener('scroll', this.handleScroll);
    
    // Restaurer le scroll si modal ouvert
    if (this.selectedImage) {
      document.body.style.overflow = 'auto';
    }
  },
  methods: {
    // Gère le clic sur une image - peut être modal ou événement personnalisé
    handleImageClick(image, index) {
      const imageObj = typeof image === 'string' ? { src: image } : image;
      
      // Si c'est un aperçu de thème, émettre un événement au lieu d'ouvrir le modal
      if (imageObj.isThemePreview) {
        this.$emit('image-click', imageObj, index);
      } else {
        // Comportement normal : ouvrir le modal
        this.openModal(this.getImageSrc(image), index);
      }
    },

    // Récupère l'URL de l'image (compatible string ou objet)
    getImageSrc(image) {
      return typeof image === 'string' ? image : image.src;
    },

    // Récupère le titre de l'image (si c'est un objet avec title)
    getImageTitle(image) {
      return typeof image === 'object' && image.title ? image.title : null;
    },

    // Méthode pour le bouton retour
    goBack() {
      this.$emit('back');
    },

    // Méthodes pour le bouton retour aux thèmes (affiché après scroll)
    handleScroll() {
      this.showScrollToTop = window.scrollY > 300;
    },

    openModal(imageSrc, index) {
      this.selectedImage = imageSrc;
      this.selectedImageIndex = index;
      document.body.style.overflow = 'hidden'; // Empêcher le scroll
    },

    closeModal() {
      this.selectedImage = null;
      this.selectedImageIndex = -1;
      this.modalImages = []; // Vider les images du modal
      document.body.style.overflow = 'auto'; // Restaurer le scroll
    },

    previousImage() {
      if (this.selectedImageIndex > 0) {
        this.selectedImageIndex--;
        this.selectedImage = this.getImageSrc(this.activeModalImages[this.selectedImageIndex]);
      }
    },

    nextImage() {
      if (this.selectedImageIndex < this.activeModalImages.length - 1) {
        this.selectedImageIndex++;
        this.selectedImage = this.getImageSrc(this.activeModalImages[this.selectedImageIndex]);
      }
    },

    // Nouvelle méthode pour ouvrir le modal avec des images spécifiques
    openModalWithImages(images, startIndex = 0) {
      this.modalImages = images;
      this.selectedImageIndex = startIndex;
      this.selectedImage = this.getImageSrc(images[startIndex]);
      document.body.style.overflow = 'hidden'; // Empêcher le scroll
    },

    handleKeydown(event) {
      if (this.selectedImage) {
        switch (event.key) {
          case 'Escape':
            this.closeModal();
            break;
          case 'ArrowLeft':
            this.previousImage();
            break;
          case 'ArrowRight':
            this.nextImage();
            break;
        }
      }
    }
  }
}
</script>

<style scoped>
/* Animation pulse pour l'indicateur */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

/* Transition pour le bouton retour aux thèmes */
.fade-enter-active, .fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.8);
}

.fade-enter-to, .fade-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}
</style>